import { getScrollParentElement } from "@regrello/core-utils";
import { useAtom } from "jotai";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDragDropManager } from "react-dnd";
import { v4 as uuid } from "uuid";

import { activeDndScrollableContainersAtom } from "../../../state/applicationState";

/** The height of the area that will trigger scrolling when dragged over. */
const HEIGHT_PX = 150;

/**
 * The velocity (in pixels / frame) of how fast the region will scroll when a drag approaches the
 * top or bottom boundary of the scroll container.
 */
const DELTA_PX = 2;

export interface RegrelloDndScrollableContainerProps {
  children: React.ReactNode;
}

/**
 * A wrapper component that auto-scrolls when child components are dragged using React DND (for
 * greppability: `react-dnd`).
 *
 * ___Warning:___ Currently, this component assumes that a parent element is scrollable and will
 * autoscroll that element, not an element managed internally. This is due to realities of how our
 * app is structured.
 */
// (clewis): Inspired by https://github.com/react-dnd/react-dnd/issues/3482#issuecomment-1639893170
export const RegrelloDndScrollableContainer = React.memo(function RegrelloDndScrollableContainerFn({
  children,
}: RegrelloDndScrollableContainerProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [activeDndScrollableContainers, setActiveDndScrollableContainers] = useAtom(activeDndScrollableContainersAtom);

  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();

  const rootUuidRef = useRef<string | undefined>(undefined);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout>();
  const unsubscribeRef = useRef<() => void>();

  const isActive =
    rootUuidRef.current != null &&
    activeDndScrollableContainers.length > 0 &&
    activeDndScrollableContainers[activeDndScrollableContainers.length - 1] === rootUuidRef.current;

  const setScrollInterval = useCallback((scrollableElement: HTMLElement, deltaPx: number) => {
    timerRef.current = setInterval(() => {
      scrollableElement.scrollBy(0, deltaPx);
    }, 0);
  }, []);

  // Update isDragging when any drag-monitor state changes.
  useEffect(() => {
    if (!isActive) {
      return;
    }

    const unsubscribe = monitor.subscribeToStateChange(() => {
      if (monitor.isDragging()) {
        setIsDragging(true);
      } else if (!monitor.isDragging()) {
        setIsDragging(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [monitor, isActive]);

  // Update scroll position when the drag offset changes.
  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (isDragging) {
      const rootElement = rootRef.current;
      if (rootElement == null) {
        return;
      }

      const scrollableElement = getScrollParentElement(rootElement);
      if (scrollableElement == null) {
        return;
      }

      // (clewis): Don't bother listening for resizes to the scroll region during a drag. This helps
      // us improve performance by avoiding extra DOM reads on each scroll-animation tick.
      const { top: scrollableElementTop, bottom: scrollableElementBottom } = scrollableElement.getBoundingClientRect();

      unsubscribeRef.current = monitor.subscribeToOffsetChange(() => {
        const dragOffset = monitor.getClientOffset();

        if (dragOffset == null) {
          return;
        }

        const isInUpperRegion = dragOffset.y < scrollableElementTop + HEIGHT_PX;
        const isInLowerRegion = dragOffset.y > scrollableElementBottom - HEIGHT_PX;

        const deltaPx = isInUpperRegion ? -DELTA_PX : isInLowerRegion ? DELTA_PX : 0;

        if (timerRef.current != null) {
          clearInterval(timerRef.current);
        }
        if (deltaPx !== 0) {
          setScrollInterval(scrollableElement, deltaPx);
        }
      });
    } else if (unsubscribeRef.current != null) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      unsubscribeRef.current();
    }
  }, [isDragging, monitor, setScrollInterval, isActive]);

  useEffect(() => {
    rootUuidRef.current = uuid();
    setActiveDndScrollableContainers((prevContainers) => {
      if (rootUuidRef.current == null) {
        return prevContainers;
      }

      const newContainers = [...prevContainers];
      newContainers.push(rootUuidRef.current);
      return newContainers;
    });

    return () => {
      setActiveDndScrollableContainers((prevContainers) => {
        const newContainers = [...prevContainers];
        newContainers.pop();
        return newContainers;
      });
    };
  }, [setActiveDndScrollableContainers]);

  return <div ref={rootRef}>{children}</div>;
});
